import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { awsConfig } from "../aws-config";
import { getAuthHeader, getAvailability, getReviews, createReview, respondToReview, getPortfolio, voteReviewHelpful } from "../api";
import BookingModal from "../components/BookingModal";
import ReportModal from "../components/ReportModal";
import { formatRelative } from "../utils/dateFormat";

interface Pro {
  proId: string;
  name: string;
  businessName?: string;
  subcategories?: string[];
  servicePricing?: any;
  bio: string;
  serviceCategory: string;
  hourlyRate: number;
  rating: number;
  verified: boolean;
  city: string;
  experience: string;
  responseTime: string;
  photoUrl?: string;
  phone?: string;
  email?: string;
  yearsInBusiness?: string;
  employees?: string;
  languages?: string;
  certifications?: string;
  businessHours?: string;
  about?: string;
  serviceArea?: string;
  website?: string;
  instagram?: string;
  acceptanceRate?: number;
  hireCount?: number;
  isOnline?: boolean;
}

const TABS = ["About", "Services", "Portfolio", "Reviews", "Q&A", "Credentials", "Details"];

export default function ProProfilePage() {
  const navigate = useNavigate();
  const { proId } = useParams<{ proId: string }>();
  const [pro, setPro] = useState<Pro | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("About");
  const [availability, setAvailability] = useState<any>(null);
  const [reviews, setReviews] = useState<any[]>([]);
  const [reportTarget, setReportTarget] = useState<{ type: "pro" | "review"; id: string; name?: string } | null>(null);
  const [portfolio, setPortfolio] = useState<any[]>([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewTags, setReviewTags] = useState<string[]>([]);
  const [votedReviews, setVotedReviews] = useState<Set<string>>(new Set());
  const [reviewText, setReviewText] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [respondingTo, setRespondingTo] = useState<string | null>(null);
  const [responseText, setResponseText] = useState("");
  const tabsRef = useRef<HTMLDivElement>(null);
  const [stickyTabs, setStickyTabs] = useState(false);
  const [showBooking, setShowBooking] = useState(false);

  const currentUserId = (() => {
    const k = Object.keys(localStorage).find(key => key.includes("idToken"));
    if (!k) return "";
    const t = localStorage.getItem(k);
    if (!t) return "";
    try { return JSON.parse(atob(t.split(".")[1])).sub || ""; } catch { return ""; }
  })();
  const isOwnProfile = currentUserId === proId;

  useEffect(() => {
    async function load() {
      try {
        const headers = await getAuthHeader();
        const response = await axios.get(`${awsConfig.apiUrl}/experts`, { headers });
        const found = response.data.experts?.find((e: Pro) => e.proId === proId);
        setPro(found || null);
        if (found?.proId) {
          getAvailability(found.proId).then(a => setAvailability(a)).catch(() => {});
          getReviews(found.proId).then(r => setReviews(r || [])).catch(() => {});
          getPortfolio(found.proId).then(p => setPortfolio(p || [])).catch(() => {});
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }
    load();
  }, [proId]);

  useEffect(() => {
    const handleScroll = () => {
      if (tabsRef.current) {
        setStickyTabs(tabsRef.current.getBoundingClientRect().top <= 0);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  function getClientId() {
    const k = Object.keys(localStorage).find(key => key.includes("idToken"));
    if (!k) return "client";
    const t = localStorage.getItem(k);
    if (!t) return "client";
    try { return JSON.parse(atob(t.split(".")[1])).sub; } catch { return "client"; }
  }

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-mouna-blue border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  if (!pro) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="text-4xl mb-3">😕</div>
        <div className="font-bold text-navy">Pro not found</div>
        <button onClick={() => navigate("/explore")} className="text-mouna-blue text-sm mt-2 block">← Back to Explore</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="bg-white">
        {/* Cover */}
        <div className="h-36 bg-gradient-to-br from-navy via-mouna-blue to-blue-600 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{backgroundImage: "radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)", backgroundSize: "30px 30px"}}></div>
          <button onClick={() => navigate(-1)}
            className="absolute top-4 left-4 bg-white bg-opacity-20 backdrop-blur-sm text-white rounded-full px-3 py-2 text-sm font-bold flex items-center gap-1">
            ← Back
          </button>
          {pro.isOnline && (
            <div className="absolute top-4 right-4 bg-teal text-white text-xs font-bold rounded-full px-3 py-1.5 flex items-center gap-1.5">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              Online Now
            </div>
          )}
        </div>

        {/* Hero content - 3 column on desktop, stacked mobile */}
        <div className="px-5 pb-5">
          {/* Photo + name row */}
          <div className="flex items-end gap-4 -mt-12 mb-4">
            <div className="w-24 h-24 rounded-2xl border-4 border-white shadow-xl overflow-hidden bg-blue-pale flex-shrink-0">
              {pro.photoUrl
                ? <img src={pro.photoUrl} alt={pro.name} className="w-full h-full object-cover"/>
                : <div className="w-full h-full flex items-center justify-center text-4xl">👷</div>}
            </div>
            <div className="pb-1 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                {pro.verified && (
                  <span className="bg-teal text-white text-xs font-bold rounded-full px-2 py-0.5">✓ VERIFIED</span>
                )}
                {(pro.acceptanceRate || 0) >= 90 && (
                  <span className="bg-gold text-white text-xs font-bold rounded-full px-2 py-0.5">⭐ TOP PRO</span>
                )}
              </div>
              <h1 className="font-display text-xl font-bold text-navy leading-tight">{pro.businessName || pro.name}</h1>
              {pro.businessName && pro.businessName !== pro.name && (
                <div className="text-xs text-gray-400">by {pro.name}</div>
              )}
              <div className="text-xs text-mouna-blue font-semibold mt-0.5">{pro.serviceCategory}</div>
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            {[
              { value: pro.rating ? `⭐ ${pro.rating}` : "New", label: "Rating", highlight: true },
              { value: pro.hireCount || 0, label: "Hires" },
              { value: pro.acceptanceRate ? `${pro.acceptanceRate}%` : "—", label: "Accepts" },
              { value: pro.responseTime || "—", label: "Response" },
            ].map(stat => (
              <div key={stat.label} className={"text-center rounded-2xl py-3 " + (stat.highlight ? "bg-mouna-blue text-white" : "bg-gray-50")}>
                <div className={"font-bold text-sm " + (stat.highlight ? "text-white" : "text-navy")}>{stat.value}</div>
                <div className={"text-xs " + (stat.highlight ? "text-blue-200" : "text-gray-400")}>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Category + location chips */}
          <div className="flex gap-2 flex-wrap mb-4">
            <span className="bg-blue-pale text-mouna-blue text-xs font-semibold rounded-full px-3 py-1">{pro.serviceCategory}</span>
            {pro.city && <span className="bg-gray-100 text-gray-600 text-xs font-semibold rounded-full px-3 py-1">📍 {pro.city}</span>}
            {pro.experience && <span className="bg-gray-100 text-gray-600 text-xs font-semibold rounded-full px-3 py-1">⏱ {pro.experience}</span>}
            {pro.hourlyRate && <span className="bg-gray-100 text-gray-600 text-xs font-semibold rounded-full px-3 py-1">${pro.hourlyRate}/hr</span>}
          </div>

          {/* CTAs */}
          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/messages?conversation=${getClientId()}_${pro.proId}&recipient=${pro.proId}&name=${pro.name}`)}
              className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-xl hover:bg-gray-200 transition-colors flex-shrink-0"
              title="Message">
              💬
            </button>
            <button
              onClick={() => navigate(`/quote?proId=${pro.proId}&category=${pro.serviceCategory}&name=${pro.name}`)}
              className="flex-1 px-4 py-3 rounded-full bg-gray-100 text-navy font-bold text-sm hover:bg-gray-200 transition-colors">
              Get Quote
            </button>
            <button
              onClick={() => {
                if (!currentUserId) {
                  navigate("/login?redirect=" + encodeURIComponent("/pro/" + proId) + "&action=book");
                } else {
                  setShowBooking(true);
                }
              }}
              className="flex-1 px-4 py-3 rounded-full bg-mouna-blue text-white font-bold text-sm hover:bg-blue-700 transition-colors">
              Book Now
            </button>
          </div>
        </div>
      </div>

      {/* Sticky tabs */}
      <div ref={tabsRef} className={`bg-white border-b border-gray-100 ${stickyTabs ? "sticky top-0 z-30 shadow-sm" : ""}`}>
        <div className="flex overflow-x-auto px-4 gap-0 scrollbar-hide">
          {TABS.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={"py-3.5 px-4 text-xs font-bold whitespace-nowrap border-b-2 transition-all flex-shrink-0 " +
                (activeTab === tab ? "border-mouna-blue text-mouna-blue" : "border-transparent text-gray-400 hover:text-gray-600")}>
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className="px-5 pt-4 pb-24">

        {/* About Tab */}
        {activeTab === "About" && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h2 className="font-bold text-navy mb-2">About</h2>
              <p className="text-sm text-gray-600 leading-relaxed">{pro.about || pro.bio || "No bio yet."}</p>
            </div>
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h2 className="font-bold text-navy mb-3">Quick Facts</h2>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "🏢", label: "Years in Business", value: pro.yearsInBusiness },
                  { icon: "👥", label: "Team Size", value: pro.employees },
                  { icon: "🌍", label: "Languages", value: pro.languages },
                  { icon: "⏰", label: "Business Hours", value: pro.businessHours },
                  { icon: "📍", label: "Service Area", value: pro.serviceArea },
                  { icon: "💰", label: "Hourly Rate", value: pro.hourlyRate ? `$${pro.hourlyRate}/hr` : null },
                ].filter(f => f.value).map(fact => (
                  <div key={fact.label} className="flex items-start gap-2">
                    <span className="text-lg">{fact.icon}</span>
                    <div>
                      <div className="text-xs text-gray-400">{fact.label}</div>
                      <div className="text-xs font-semibold text-navy">{fact.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {availability && (
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <h2 className="font-bold text-navy mb-3">Availability</h2>
                {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map(day => (
                  <div key={day} className="flex justify-between items-center py-1.5 border-b border-gray-50 last:border-0">
                    <span className="text-xs text-gray-500">{day}</span>
                    {availability[day]?.available
                      ? <span className="text-xs font-semibold text-teal">{availability[day].start} – {availability[day].end}</span>
                      : <span className="text-xs text-gray-400">Unavailable</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Services Tab */}
        {activeTab === "Services" && (
          <div className="space-y-3">
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 bg-blue-pale rounded-xl flex items-center justify-center text-xl">⚡</div>
                <div className="flex-1">
                  <div className="font-bold text-navy">{pro.serviceCategory}</div>
                  <div className="text-xs text-gray-400">Specialized in {(pro.subcategories || []).length} services</div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-mouna-blue">${pro.hourlyRate}/hr</div>
                  <div className="text-xs text-gray-400">Base rate</div>
                </div>
              </div>
            </div>

            {(pro.subcategories || []).length > 0 ? (
              <div className="space-y-2">
                <div className="text-xs text-gray-400 font-bold uppercase tracking-wider px-1">Services Offered</div>
                {(pro.subcategories || []).map((sub: string) => {
                  const pricing = (pro.servicePricing || {})[sub];
                  return (
                    <div key={sub} className="bg-white rounded-2xl p-4 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="w-1.5 h-1.5 bg-teal rounded-full"></span>
                            <div className="font-bold text-navy text-sm">{sub}</div>
                          </div>
                          {pricing?.description && (
                            <div className="text-xs text-gray-400 ml-3.5">{pricing.description}</div>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0 ml-3">
                          <div className="font-bold text-mouna-blue text-sm">
                            ${pricing?.price || pro.hourlyRate}
                          </div>
                          <div className="text-xs text-gray-400">/{pricing?.unit || "hour"}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-blue-pale rounded-2xl p-4 text-center">
                <div className="text-3xl mb-2">📋</div>
                <div className="text-sm text-gray-500">Specific services not listed yet</div>
              </div>
            )}

            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <div className="text-xs text-gray-400 mb-2">💬 Need something custom?</div>
              <div className="text-sm text-gray-600 mb-3">Message {(pro.businessName || pro.name).split(" ")[0]} for a custom quote on your specific project.</div>
              <button onClick={() => navigate(`/quote?proId=${pro.proId}&category=${pro.serviceCategory}&name=${pro.businessName || pro.name}`)}
                className="w-full bg-mouna-blue text-white rounded-full py-3 text-sm font-bold">
                Request Custom Quote
              </button>
            </div>
          </div>
        )}

        {/* Portfolio Tab */}
        {activeTab === "Portfolio" && (
          <div>
            {portfolio.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-4xl mb-3">📸</div>
                <div className="font-bold text-navy mb-1">No portfolio yet</div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {portfolio.map((item: any) => (
                  <div key={item.id} className="bg-white rounded-2xl overflow-hidden shadow-sm">
                    <div className="aspect-square bg-gray-100">
                      {item.type === "video"
                        ? <video src={item.url} className="w-full h-full object-cover" controls/>
                        : <img src={item.url} alt={item.caption || "Portfolio"} className="w-full h-full object-cover"/>}
                    </div>
                    {item.caption && <div className="p-2 text-xs text-gray-600">{item.caption}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === "Reviews" && (
          <div>
            <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
              <div className="flex items-center gap-4">
                <div className="text-5xl font-bold text-navy">{pro.rating || "—"}</div>
                <div>
                  <div className="text-gold text-xl">{"★".repeat(Math.round(pro.rating || 0))}{"☆".repeat(5 - Math.round(pro.rating || 0))}</div>
                  <div className="text-xs text-gray-400">{reviews.length} review{reviews.length !== 1 ? "s" : ""}</div>
                </div>
              </div>
            </div>

            {!showReviewForm && !isOwnProfile && (
              <button onClick={() => setShowReviewForm(true)}
                className="w-full bg-blue-pale text-mouna-blue rounded-2xl py-3 font-bold text-sm mb-4">
                ★ Leave a Review
              </button>
            )}

            {showReviewForm && (
              <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
                <h3 className="font-bold text-navy mb-3">Your Review</h3>
                <div className="flex gap-2 mb-3">
                  {[1,2,3,4,5].map(star => (
                    <button key={star} onClick={() => setReviewRating(star)}
                      className={"text-2xl " + (star <= reviewRating ? "text-gold" : "text-gray-300")}>★</button>
                  ))}
                </div>
                <textarea value={reviewText} onChange={e => setReviewText(e.target.value)}
                  placeholder="Share your experience..." rows={3}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-mouna-blue resize-none mb-3"/>
                <div className="mb-3">
                  <div className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2">What stood out?</div>
                  <div className="flex flex-wrap gap-2">
                    {["Professional", "On-time", "Quality work", "Great value", "Clean", "Communication", "Friendly", "Knowledgeable"].map(tag => (
                      <button key={tag} onClick={() => setReviewTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag])}
                        className={"px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-all " +
                          (reviewTags.includes(tag) ? "bg-mouna-blue text-white border-mouna-blue" : "bg-white text-gray-500 border-gray-200")}>
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setShowReviewForm(false); setReviewTags([]); }}
                    className="flex-1 bg-gray-100 text-gray-500 rounded-full py-2 text-sm">Cancel</button>
                  <button onClick={async () => {
                    if (!reviewText.trim()) return;
                    setSubmittingReview(true);
                    try {
                      await createReview(pro!.proId, reviewRating, reviewText, "", reviewTags, []);
                      const updated = await getReviews(pro!.proId);
                      setReviews(updated || []);
                      setShowReviewForm(false);
                      setReviewText(""); setReviewRating(5); setReviewTags([]);
                    } catch(e: any) {
                      if (e.response?.status === 409) alert("You already reviewed this booking");
                      else console.error(e);
                    }
                    finally { setSubmittingReview(false); }
                  }} disabled={submittingReview}
                    className="flex-1 bg-mouna-blue text-white rounded-full py-2 text-sm font-bold">
                    {submittingReview ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </div>
            )}

            {reviews.length === 0 && !showReviewForm && (
              <div className="text-center py-8">
                <div className="text-3xl mb-2">💬</div>
                <div className="text-sm text-gray-400">Be the first to leave a review</div>
              </div>
            )}

            {reviews.map(review => (
              <div key={review.reviewId} className="bg-white rounded-2xl p-4 shadow-sm mb-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-pale flex items-center justify-center text-xs font-bold text-mouna-blue">
                      {(review.clientName || review.clientEmail)?.[0]?.toUpperCase() || "C"}
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-semibold text-navy">{review.clientName || review.clientEmail?.split("@")[0] || "Client"}</span>
                        {review.verified && (
                          <span className="bg-teal-light text-teal text-xs font-bold rounded-full px-2 py-0.5 flex items-center gap-1">
                            ✓ Verified
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <span className="text-gold text-sm">{"★".repeat(review.rating)}</span>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed mb-2">{review.review}</p>
                {(review.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {review.tags.map((tag: string) => (
                      <span key={tag} className="bg-blue-pale text-mouna-blue text-xs font-semibold rounded-full px-2.5 py-0.5">{tag}</span>
                    ))}
                  </div>
                )}
                {(review.photoUrls || []).length > 0 && (
                  <div className="flex gap-2 mb-2 overflow-x-auto">
                    {review.photoUrls.map((url: string, i: number) => (
                      <img key={i} src={url} alt="" className="w-20 h-20 rounded-xl object-cover flex-shrink-0"/>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-400">{formatRelative(review.createdAt)}</div>
                  <button onClick={async () => {
                    if (votedReviews.has(review.reviewId)) return;
                    try {
                      await voteReviewHelpful(review.reviewId);
                      setVotedReviews(prev => new Set(prev).add(review.reviewId));
                      const updated = await getReviews(pro!.proId);
                      setReviews(updated || []);
                    } catch(e) {}
                  }}
                    className={"text-xs font-bold flex items-center gap-1 px-2.5 py-1 rounded-full transition-colors " +
                      (votedReviews.has(review.reviewId) ? "bg-teal-light text-teal" : "bg-gray-50 text-gray-400 hover:bg-gray-100")}>
                    👍 Helpful {(review.helpfulCount || 0) > 0 && `(${review.helpfulCount})`}
                  </button>
                  {!isOwnProfile && (
                    <button onClick={() => setReportTarget({ type: "review", id: review.reviewId, name: "review" })}
                      className="text-xs font-bold flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-50 text-gray-400 hover:bg-gray-100 transition-colors">
                      🚩 Report
                    </button>
                  )}
                </div>
                {review.proResponse && (
                  <div className="mt-3 bg-blue-pale rounded-xl p-3">
                    <div className="text-xs font-bold text-mouna-blue mb-1">Response from {pro.name.split(" ")[0]}</div>
                    <div className="text-xs text-gray-600">{review.proResponse}</div>
                  </div>
                )}
                {isOwnProfile && !review.proResponse && respondingTo !== review.reviewId && (
                  <button onClick={() => { setRespondingTo(review.reviewId); setResponseText(""); }}
                    className="mt-2 text-xs text-mouna-blue font-bold">+ Respond</button>
                )}
                {isOwnProfile && respondingTo === review.reviewId && (
                  <div className="mt-3">
                    <textarea value={responseText} onChange={e => setResponseText(e.target.value)}
                      placeholder="Write your response..." rows={2}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs outline-none focus:border-mouna-blue resize-none mb-2"/>
                    <div className="flex gap-2">
                      <button onClick={() => setRespondingTo(null)}
                        className="flex-1 bg-gray-100 text-gray-500 rounded-full py-1.5 text-xs">Cancel</button>
                      <button onClick={async () => {
                        if (!responseText.trim()) return;
                        await respondToReview(review.reviewId, responseText);
                        const updated = await getReviews(pro!.proId);
                        setReviews(updated || []);
                        setRespondingTo(null);
                      }} className="flex-1 bg-mouna-blue text-white rounded-full py-1.5 text-xs font-bold">Post</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Q&A Tab */}
        {activeTab === "Q&A" && (
          <div>
            <div className="text-center py-12">
              <div className="text-4xl mb-3">💬</div>
              <div className="font-bold text-navy mb-1">Have a question for {pro.name}?</div>
              <div className="text-xs text-gray-500 max-w-xs mx-auto">Ask about availability, pricing, supplies, or anything else — message them directly.</div>
            </div>
            <button onClick={() => navigate(`/messages?conversation=${getClientId()}_${pro!.proId}&recipient=${pro!.proId}&name=${pro!.name}`)} className="w-full bg-blue-pale text-mouna-blue rounded-2xl py-3 font-bold text-sm">
              💬 Ask a Question
            </button>
          </div>
        )}

        {/* Credentials Tab */}
        {activeTab === "Credentials" && (
          <div className="space-y-4">
            {pro.certifications ? (
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <h2 className="font-bold text-navy mb-3">Certifications & Licenses</h2>
                <p className="text-sm text-gray-600 whitespace-pre-line">{pro.certifications}</p>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-4xl mb-3">📋</div>
                <div className="font-bold text-navy mb-1">No credentials added yet</div>
              </div>
            )}
            {pro.verified && (
              <div className="bg-teal-light rounded-2xl p-4 flex items-center gap-3">
                <span className="text-2xl">✓</span>
                <div>
                  <div className="font-bold text-teal text-sm">Mouna Verified Pro</div>
                  <div className="text-xs text-gray-500">Email-verified and approved by the Mouna team</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Details Tab */}
        {activeTab === "Details" && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h2 className="font-bold text-navy mb-3">Business Information</h2>
              {[
                { label: "Business Hours", value: pro.businessHours },
                { label: "Service Area", value: pro.serviceArea },
                { label: "Languages", value: pro.languages },
                { label: "Team Size", value: pro.employees },
                { label: "Years in Business", value: pro.yearsInBusiness },
              ].filter(i => i.value).map(item => (
                <div key={item.label} className="flex justify-between py-2 border-b border-gray-50 last:border-0">
                  <span className="text-xs text-gray-400">{item.label}</span>
                  <span className="text-xs font-semibold text-navy">{item.value}</span>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h2 className="font-bold text-navy mb-3">Contact & Social</h2>
              {pro.phone && (
                <div className="flex justify-between py-2 border-b border-gray-50">
                  <span className="text-xs text-gray-400">Phone</span>
                  <span className="text-xs font-semibold text-navy">{pro.phone}</span>
                </div>
              )}
              {pro.website && (
                <div className="flex justify-between py-2 border-b border-gray-50">
                  <span className="text-xs text-gray-400">Website</span>
                  <a href={pro.website} target="_blank" rel="noreferrer" className="text-xs font-semibold text-mouna-blue">{pro.website}</a>
                </div>
              )}
              {pro.instagram && (
                <div className="flex justify-between py-2">
                  <span className="text-xs text-gray-400">Instagram</span>
                  <span className="text-xs font-semibold text-navy">@{pro.instagram}</span>
                </div>
              )}
            </div>
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h2 className="font-bold text-navy mb-2">Cancellation Policy</h2>
              <p className="text-xs text-gray-600">Cancellation policies vary by pro — confirm the details with {pro.name.split(" ")[0]} before booking.</p>
            </div>
          </div>
        )}

        {!isOwnProfile && (
          <div className="text-center py-6">
            <button onClick={() => setReportTarget({ type: "pro", id: pro.proId, name: pro.name })}
              className="text-xs text-gray-400 hover:text-red-500 font-semibold transition-colors">
              🚩 Report this pro
            </button>
          </div>
        )}
      </div>
      {reportTarget && (
        <ReportModal targetType={reportTarget.type} targetId={reportTarget.id} targetName={reportTarget.name}
          onClose={() => setReportTarget(null)}/>
      )}
      {showBooking && pro && (
        <BookingModal
          expert={{
            proId: pro.proId,
            name: pro.name,
            businessName: pro.businessName,
            serviceCategory: pro.serviceCategory,
            subcategories: pro.subcategories || [],
            hourlyRate: pro.hourlyRate || 0,
          }}
          onClose={() => setShowBooking(false)}
          onBooked={() => { setShowBooking(false); navigate("/bookings"); }}
        />
      )}
    </div>
  );
}
